// src/pages/settings/sms/components/GatewaySetupPanel.jsx
import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Trash2, ChevronDown, Plus, X, AlertCircle } from 'lucide-react';
import { getShops } from '../../../../services/api/shops'; // adjust import to your actual shops API
import styles from './GatewaySetupPanel.module.css';

const GatewaySetupPanel = ({
  gateways,
  config,
  isSaving,
  onSelectGateway,
  onUpdateConfig,
  onDeleteConfig,
}) => {
  const [selectedGatewayId, setSelectedGatewayId] = useState('');
  const [apiConfigForm, setApiConfigForm] = useState({});
  const [isActive, setIsActive] = useState(false);
  const [configShopWise, setConfigShopWise] = useState(false);
  const [shopConfig, setShopConfig] = useState({});
  const [shops, setShops] = useState([]);
  const [addedShopIds, setAddedShopIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Derive selected gateway definition
  const gateway = config
    ? gateways.find(g => g.id === config.smsGatewayId)
    : gateways.find(g => g.id === parseInt(selectedGatewayId));

  const additionalFields = gateway?.apiConfig?.additionalFields || {};
  const shopRelatedFields = gateway?.apiConfig?.shopRelatedFields || {};
  const hasShopFields = Object.keys(shopRelatedFields).length > 0;

  // Sync form state when config loads
  useEffect(() => {
    if (config) {
      setIsActive(config.isActive || false);
      setConfigShopWise(config.configShopWise || false);
      setApiConfigForm(config.apiConfig || {});
      const sc = config.shopConfig || {};
      setShopConfig(sc);
      setAddedShopIds(Object.keys(sc).map(Number));
    }
  }, [config]);

  // Load shops when configShopWise is toggled on
  useEffect(() => {
    if (configShopWise && shops.length === 0) {
      getShops().then(res => setShops(res.shops || res || [])).catch(() => {});
    }
  }, [configShopWise]);

  const handleAdditionalFieldChange = (field, value) => {
    setApiConfigForm(prev => ({ ...prev, [field]: value }));
  };

  const handleShopFieldChange = (shopId, field, value) => {
    setShopConfig(prev => ({
      ...prev,
      [shopId]: { ...(prev[shopId] || {}), [field]: value },
    }));
  };

  const handleAddShop = (shopId) => {
    if (!addedShopIds.includes(shopId)) {
      setAddedShopIds(prev => [...prev, shopId]);
    }
  };

  const handleRemoveShop = (shopId) => {
    setAddedShopIds(prev => prev.filter(id => id !== shopId));
    setShopConfig(prev => {
      const next = { ...prev };
      delete next[shopId];
      return next;
    });
  };

  const handleSave = () => {
    const payload = {
      isActive,
      configShopWise,
      apiConfig: apiConfigForm,
    };
    if (configShopWise) {
      // Only persist shops that have been added
      const filteredShopConfig = {};
      addedShopIds.forEach(id => {
        filteredShopConfig[id] = shopConfig[id] || {};
      });
      payload.shopConfig = filteredShopConfig;
    } else {
      payload.shopConfig = {};
    }
    onUpdateConfig(payload);
  };

  const renderField = (fieldName, fieldDef, value, onChange) => {
    switch (fieldDef.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={e => onChange(fieldName, e.target.value)}
            placeholder={`Enter ${fieldDef.label}`}
            className={styles.input}
          />
        );
      case 'password':
        return (
          <input
            type="password"
            value={value || ''}
            onChange={e => onChange(fieldName, e.target.value)}
            placeholder={`Enter ${fieldDef.label}`}
            className={styles.input}
          />
        );
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={e => onChange(fieldName, e.target.value)}
            className={styles.select}
          >
            <option value="">Select {fieldDef.label}</option>
            {(fieldDef.options || []).map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'checkbox':
        return (
          <label className={styles.toggleLabel}>
            <span>{fieldDef.label}</span>
            <div className={styles.toggleWrapper}>
              <input
                type="checkbox"
                checked={!!value}
                onChange={e => onChange(fieldName, e.target.checked)}
                className={styles.toggleInput}
              />
              <span className={styles.toggleSlider} />
            </div>
          </label>
        );
      default:
        return null;
    }
  };

  // ── No config yet: show gateway picker ──────────────────────────────────────
  if (!config) {
    return (
      <div className={styles.panel}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Select SMS Gateway</h3>
          <p className={styles.sectionHint}>Choose a gateway to get started. You can configure credentials after selecting.</p>
          <div className={styles.gatewayGrid}>
            {gateways.map(gw => (
              <div
                key={gw.id}
                className={`${styles.gatewayCard} ${selectedGatewayId === String(gw.id) ? styles.gatewayCardSelected : ''}`}
                onClick={() => setSelectedGatewayId(String(gw.id))}
              >
                <div className={styles.gatewayName}>{gw.name}</div>
                <div className={styles.gatewayMeta}>Max {gw.smsLength} chars</div>
              </div>
            ))}
          </div>
          <button
            className={styles.primaryBtn}
            disabled={!selectedGatewayId || isSaving}
            onClick={() => onSelectGateway(parseInt(selectedGatewayId))}
          >
            {isSaving ? <><RefreshCw size={16} className={styles.spin} /> Adding...</> : <><Plus size={16} /> Add Gateway</>}
          </button>
        </div>
      </div>
    );
  }

  // ── Config exists: show full form ────────────────────────────────────────────
  return (
    <div className={styles.panel}>
      {/* Active toggle */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Gateway: {gateway?.name || `#${config.smsGatewayId}`}</h3>
        <div className={styles.formGroup}>
          <label className={styles.toggleRow}>
            <span className={styles.toggleText}>Active</span>
            <div className={styles.toggleWrapper}>
              <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className={styles.toggleInput} />
              <span className={styles.toggleSlider} />
            </div>
          </label>
          <small className={styles.hint}>Enable to allow SMS sending for this business</small>
        </div>
      </div>

      {/* Credentials (additionalFields) */}
      {Object.keys(additionalFields).length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>API Credentials</h3>
          {Object.entries(additionalFields).map(([field, def]) => (
            <div key={field} className={styles.formGroup}>
              <label className={styles.label}>{def.label}</label>
              {renderField(field, def, apiConfigForm[field], handleAdditionalFieldChange)}
            </div>
          ))}
        </div>
      )}

      {/* Shop-wise config */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Shop Configuration</h3>
        <div className={styles.formGroup}>
          <label className={styles.toggleRow}>
            <span className={styles.toggleText}>Configure per shop</span>
            <div className={styles.toggleWrapper}>
              <input type="checkbox" checked={configShopWise} onChange={e => setConfigShopWise(e.target.checked)} className={styles.toggleInput} />
              <span className={styles.toggleSlider} />
            </div>
          </label>
          <small className={styles.hint}>If enabled, you can set different gateway options per shop</small>
        </div>

        {configShopWise && (
          <div className={styles.shopSection}>
            {/* Shop picker */}
            <div className={styles.shopPickerRow}>
              <select
                className={styles.select}
                onChange={e => { if (e.target.value) { handleAddShop(parseInt(e.target.value)); e.target.value = ''; } }}
                defaultValue=""
              >
                <option value="" disabled>Add a shop...</option>
                {shops
                  .filter(s => !addedShopIds.includes(s.id))
                  .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            {addedShopIds.length === 0 && (
              <p className={styles.emptyHint}>No shops added yet. Select a shop above.</p>
            )}

            {addedShopIds.map(shopId => {
              const shop = shops.find(s => s.id === shopId);
              return (
                <div key={shopId} className={styles.shopCard}>
                  <div className={styles.shopCardHeader}>
                    <span className={styles.shopName}>{shop?.name || `Shop #${shopId}`}</span>
                    <button className={styles.removeShopBtn} onClick={() => handleRemoveShop(shopId)}>
                      <X size={14} />
                    </button>
                  </div>
                  {hasShopFields ? (
                    Object.entries(shopRelatedFields).map(([field, def]) => (
                      <div key={field} className={styles.formGroup}>
                        <label className={styles.label}>{def.label}</label>
                        {renderField(
                          field,
                          def,
                          shopConfig[shopId]?.[field],
                          (f, v) => handleShopFieldChange(shopId, f, v)
                        )}
                      </div>
                    ))
                  ) : (
                    <p className={styles.emptyHint}>No per-shop fields for this gateway.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <button className={styles.dangerBtn} onClick={() => setShowDeleteConfirm(true)}>
          <Trash2 size={16} /> Remove Gateway
        </button>
        <button className={styles.primaryBtn} onClick={handleSave} disabled={isSaving}>
          {isSaving ? <><RefreshCw size={16} className={styles.spin} /> Saving...</> : <><Save size={16} /> Save Configuration</>}
        </button>
      </div>

      {/* Delete confirm */}
      {showDeleteConfirm && (
        <div className={styles.confirmOverlay} onClick={() => setShowDeleteConfirm(false)}>
          <div className={styles.confirmBox} onClick={e => e.stopPropagation()}>
            <AlertCircle size={32} className={styles.confirmIcon} />
            <h4>Remove SMS Configuration?</h4>
            <p>This will delete all triggers and cannot be undone.</p>
            <div className={styles.confirmActions}>
              <button className={styles.secondaryBtn} onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className={styles.dangerBtn} onClick={() => { setShowDeleteConfirm(false); onDeleteConfig(); }} disabled={isSaving}>
                {isSaving ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GatewaySetupPanel;